import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getEquivalentRoute } from '../data/translations';

export default function Layout({ children }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const currentPath = location.pathname;
  const isEn = currentPath.startsWith('/en');

  const daTarget = getEquivalentRoute(currentPath, 'da');
  const enTarget = getEquivalentRoute(currentPath, 'en');

  const toggleDropdown = (key) => {
    setActiveDropdown((prev) => (prev === key ? null : key));
  };

  const closeMenus = () => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  };

  // Danish Menu Structure (Matches reference website layout)
  const daMenu = [
    { label: 'Hjem', href: '/' },
    {
      label: 'VVS',
      id: 'vvs',
      href: '/vvs',
      children: [
        { label: 'VVS-installatør – alle ydelser', href: '/vvs' },
        { label: 'Badeværelser', href: '/vvs/badevaerelser' },
        { label: 'Rørinstallationer & vandskader', href: '/vvs/roerinstallationer' },
        { label: 'Ventilation & indeklima', href: '/vvs/ventilation' },
        { label: 'Akut VVS – døgnvagt', href: '/vvs/akut-vvs' }
      ]
    },
    { label: 'Badeværelser', href: '/vvs/badevaerelser' },
    { label: 'Fjernvarme', href: '/varme/fjernvarme' },
    { label: 'Varmepumper', href: '/varme/varmepumper' },
    { label: 'Blik', href: '/blikkenslager' },
    { label: 'Smedearbejde', href: '/erhverv' },
    { label: 'Service', href: '/service-og-vedligehold' },
    {
      label: 'Om os',
      id: 'omos',
      href: '/om-os',
      children: [
        { label: 'Om Billund Varme & Sanitet', href: '/om-os' },
        { label: 'Referencer', href: '/referencer' },
        { label: 'Job & lærlingepladser', href: '/karriere' },
        { label: 'Ofte stillede spørgsmål', href: '/faq' }
      ]
    },
    { label: 'Kontakt', href: '/kontakt' }
  ];

  // English Menu Structure
  const enMenu = [
    { label: 'Home', href: '/en' },
    {
      label: 'Plumbing',
      id: 'plumbing',
      href: '/en/plumbing',
      children: [
        { label: 'Plumber in Billund – all services', href: '/en/plumbing' },
        { label: 'Bathrooms', href: '/en/plumbing/bathrooms' },
        { label: 'Pipework & water damage', href: '/en/plumbing/pipework-water-damage' },
        { label: 'Ventilation & indoor climate', href: '/en/plumbing/ventilation' },
        { label: 'Emergency plumber – 24/7', href: '/en/plumbing/emergency' }
      ]
    },
    { label: 'Bathrooms', href: '/en/plumbing/bathrooms' },
    { label: 'District heating', href: '/en/heating/district-heating' },
    { label: 'Heat pumps', href: '/en/heating/heat-pumps' },
    { label: 'Sheet metal', href: '/en/sheet-metal' },
    { label: 'Metalwork', href: '/en/commercial' },
    { label: 'Service', href: '/en/service-maintenance' },
    {
      label: 'About us',
      id: 'about',
      href: '/en/about',
      children: [
        { label: 'About Billund Varme & Sanitet', href: '/en/about' },
        { label: 'References', href: '/en/references' },
        { label: 'Jobs & apprenticeships', href: '/en/careers' },
        { label: 'Frequently asked questions', href: '/en/faq' }
      ]
    },
    { label: 'Contact', href: '/en/contact' }
  ];

  const currentMenu = isEn ? enMenu : daMenu;

  return (
    <div className="min-h-screen flex flex-col font-sans text-slate-800 bg-white">
      {/* Top Utility Header */}
      <div className="border-b border-slate-200 bg-white py-2.5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4">
          {/* Logo */}
          <Link to={isEn ? '/en' : '/'} className="flex items-center gap-3 shrink-0 py-0.5">
            <img
              src="https://assets.auxodev.dk/assets/7f5366e1-5aaf-452f-9c72-7b11437bc331/chatgpt-image-23-sep-2026-10-26-14.png"
              alt="Billund Varme & Sanitet A/S"
              className="h-14 sm:h-16 md:h-20 w-auto object-contain"
            />
          </Link>

          {/* Right Utility Items - Dual Action Pills with Dark Circle Icons */}
          <div className="flex items-center gap-4 sm:gap-6 lg:gap-8">
            <a
              href="tel:+4575331276"
              className="hidden sm:inline-flex items-center gap-3 text-left group hover:opacity-90 transition-opacity"
            >
              <div className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center shrink-0 shadow-xs group-hover:bg-[#d3102e] transition-colors">
                <i className="fa-solid fa-phone text-sm" aria-hidden="true"></i>
              </div>
              <div>
                <div className="text-sm font-bold text-slate-900 leading-tight">75 33 12 76</div>
                <div className="text-xs text-slate-500 leading-tight mt-0.5">{isEn ? 'Call us' : 'Ring til os'}</div>
              </div>
            </a>

            <a
              href="mailto:info@billundvvs.dk"
              className="hidden md:inline-flex items-center gap-3 text-left group hover:opacity-90 transition-opacity"
            >
              <div className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center shrink-0 shadow-xs group-hover:bg-[#d3102e] transition-colors">
                <i className="fa-solid fa-envelope text-sm" aria-hidden="true"></i>
              </div>
              <div>
                <div className="text-sm font-bold text-slate-900 leading-tight">info@billundvvs.dk</div>
                <div className="text-xs text-slate-500 leading-tight mt-0.5">{isEn ? 'Write to us' : 'Skriv til os'}</div>
              </div>
            </a>

            {/* Language flags */}
            <div className="flex items-center gap-1.5 border-l border-slate-200 pl-3 sm:pl-4">
              <Link
                to={daTarget}
                title="Dansk"
                className={`p-1 rounded transition-opacity ${!isEn ? 'opacity-100 ring-1 ring-slate-400' : 'opacity-60 hover:opacity-100'}`}
              >
                <svg className="w-5 h-3.5 rounded-[2px] shadow-xs inline-block" viewBox="0 0 37 28" aria-label="Dansk">
                  <rect width="37" height="28" fill="#d3102e" />
                  <rect width="4" height="28" x="12" fill="#fff" />
                  <rect width="37" height="4" y="12" fill="#fff" />
                </svg>
              </Link>
              <Link
                to={enTarget}
                title="English"
                className={`p-1 rounded transition-opacity ${isEn ? 'opacity-100 ring-1 ring-slate-400' : 'opacity-60 hover:opacity-100'}`}
              >
                <svg className="w-5 h-3.5 rounded-[2px] shadow-xs inline-block" viewBox="0 0 60 30" aria-label="English">
                  <clipPath id="uk-s"><path d="M0 0v30h60V0z"/></clipPath>
                  <clipPath id="uk-t"><path d="M30 15h30v15zv15H0zH0V0zV0h30z"/></clipPath>
                  <g clipPath="url(#uk-s)">
                    <path d="M0 0v30h60V0z" fill="#012169"/>
                    <path d="M0 0l60 30m0-30L0 30" stroke="#fff" strokeWidth="6"/>
                    <path d="M0 0l60 30m0-30L0 30" clipPath="url(#uk-t)" stroke="#d3102e" strokeWidth="4"/>
                    <path d="M30 0v30M0 15h60" stroke="#fff" strokeWidth="10"/>
                    <path d="M30 0v30M0 15h60" stroke="#d3102e" strokeWidth="6"/>
                  </g>
                </svg>
              </Link>
            </div>

            {/* Mobile hamburger button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-md text-slate-700 hover:text-red-600 hover:bg-slate-100"
              aria-label="Åbn menu"
            >
              <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-lg`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Main Navigation Bar (Desktop) */}
      <nav className="hidden lg:block bg-slate-900 relative z-30 shadow-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          {/* Menu items */}
          <div className="flex items-center space-x-1 xl:space-x-1.5">
            {currentMenu.map((item) => {
              const hasChildren = item.children && item.children.length > 0;
              const isActive =
                currentPath === item.href ||
                (item.href !== '/' && item.href !== '/en' && currentPath.startsWith(item.href));

              if (!hasChildren) {
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`px-3 py-3 text-xs font-bold uppercase tracking-wider transition-colors ${
                      isActive
                        ? 'text-white bg-[#d3102e]'
                        : 'text-slate-200 hover:text-white hover:bg-slate-800'
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              }

              return (
                <div
                  key={item.label}
                  className="relative group"
                  onMouseEnter={() => setActiveDropdown(item.id)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <Link
                    to={item.href || '#'}
                    onClick={(e) => {
                      if (!item.href) e.preventDefault();
                    }}
                    className={`px-3 py-3 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 transition-colors ${
                      isActive
                        ? 'text-white bg-[#d3102e]'
                        : 'text-slate-200 hover:text-white hover:bg-slate-800'
                    }`}
                  >
                    <span>{item.label}</span>
                    <i className="fa-solid fa-chevron-down text-[10px] text-slate-400 group-hover:text-white transition-transform"></i>
                  </Link>

                  {/* Dropdown Menu */}
                  <div
                    className={`absolute left-0 top-full pt-0.5 w-64 transition-all duration-150 z-50 ${
                      activeDropdown === item.id ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible pointer-events-none -translate-y-1'
                    }`}
                  >
                    <div className="bg-slate-900 border border-slate-700 rounded-b-lg shadow-xl py-2 overflow-hidden">
                      {item.children.map((child) => {
                        const childActive = currentPath === child.href;
                        return (
                          <Link
                            key={child.href}
                            to={child.href}
                            onClick={() => setActiveDropdown(null)}
                            className={`block px-4 py-2 text-xs font-medium transition-colors ${
                              childActive
                                ? 'bg-white/10 text-white font-semibold border-l-2 border-[#d3102e]'
                                : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                            }`}
                          >
                            {child.label}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right CTA Button */}
          <div className="flex items-center">
            <Link
              to={isEn ? '/en/get-a-quote' : '/tilbud'}
              className="bg-[#d3102e] hover:bg-red-700 text-white font-bold text-xs uppercase tracking-wider px-4 py-2 rounded transition-colors inline-flex items-center gap-1.5 shadow-xs"
            >
              <span>{isEn ? 'GET A QUOTE' : 'FÅ ET TILBUD'}</span>
              <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </nav>

      {/* Mobile Drawer Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-b border-slate-200 shadow-xl py-3 px-4 relative z-40 max-h-[85vh] overflow-y-auto">
          <div className="space-y-1">
            {currentMenu.map((item) => {
              const hasChildren = item.children && item.children.length > 0;
              if (!hasChildren) {
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={closeMenus}
                    className="block px-3 py-2 rounded-md text-sm font-bold uppercase tracking-wide text-slate-800 hover:bg-slate-50 hover:text-[#d3102e]"
                  >
                    {item.label}
                  </Link>
                );
              }

              const isOpen = activeDropdown === item.id;
              return (
                <div key={item.label} className="border-b border-slate-100 pb-1">
                  <button
                    type="button"
                    onClick={() => toggleDropdown(item.id)}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-md text-sm font-bold uppercase tracking-wide text-slate-800 hover:bg-slate-50 hover:text-[#d3102e]"
                  >
                    <span>{item.label}</span>
                    <i className={`fa-solid ${isOpen ? 'fa-chevron-up' : 'fa-chevron-down'} text-xs text-slate-500`}></i>
                  </button>
                  {isOpen && (
                    <div className="pl-4 pr-2 py-1 space-y-1 bg-slate-50 rounded-md">
                      {item.children.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          onClick={closeMenus}
                          className="block px-3 py-2 text-xs font-medium text-slate-700 hover:text-[#d3102e] rounded-sm"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            <div className="pt-3">
              <Link
                to={isEn ? '/en/get-a-quote' : '/tilbud'}
                onClick={closeMenus}
                className="w-full block text-center bg-slate-900 text-white font-bold text-xs uppercase tracking-wider py-3 rounded-md hover:bg-slate-800"
              >
                {isEn ? 'GET A FREE QUOTE' : 'FÅ ET UFORPLIGTENDE TILBUD'}
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Pre-Footer Value Strip */}
      <section className="bg-slate-900 text-white py-8 px-4 sm:px-6 lg:px-8 border-b border-slate-800">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
          {/* Item 1 */}
          <div className="flex items-start gap-3.5">
            <div className="w-10 h-10 rounded-full border border-slate-700 flex items-center justify-center shrink-0 text-red-500">
              <i className="fa-regular fa-clock text-base" aria-hidden="true"></i>
            </div>
            <div>
              <h3 className="font-bold text-sm text-white leading-snug">
                {isEn ? 'Emergency service – 24/7' : 'Døgnvagt – vi er klar 24/7'}
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">
                {isEn ? 'Call +45 75 33 12 76' : 'Ring 75 33 12 76'}
              </p>
            </div>
          </div>

          {/* Item 2 */}
          <div className="flex items-start gap-3.5">
            <div className="w-10 h-10 rounded-full border border-slate-700 flex items-center justify-center shrink-0 text-red-500">
              <i className="fa-regular fa-envelope text-base" aria-hidden="true"></i>
            </div>
            <div>
              <h3 className="font-bold text-sm text-white leading-snug">
                {isEn ? 'Free, no-obligation quotes' : 'Gratis og uforpligtende tilbud'}
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">
                {isEn ? 'Contact us today' : 'Kontakt os i dag'}
              </p>
            </div>
          </div>

          {/* Item 3 */}
          <div className="flex items-start gap-3.5">
            <div className="w-10 h-10 rounded-full border border-slate-700 flex items-center justify-center shrink-0 text-red-500">
              <i className="fa-solid fa-location-dot text-base" aria-hidden="true"></i>
            </div>
            <div>
              <h3 className="font-bold text-sm text-white leading-snug">
                {isEn ? 'We cover all of Billund Municipality' : 'Vi dækker hele Billund Kommune'}
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">
                {isEn ? 'and the surrounding area' : 'og omegn'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Footer (4 Columns) */}
      <footer className="bg-slate-950 text-slate-400 pt-16 pb-12 px-4 sm:px-6 lg:px-8 border-t border-slate-900 text-xs sm:text-sm">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          {/* Col 1: Company details */}
          <div className="space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-white">
              BILLUND VARME & SANITET A/S
            </h3>
            <p className="text-slate-400 text-xs leading-relaxed">
              {isEn
                ? 'Your local plumbing and heating contractor since 1963. Plumbing, heating, sheet-metal and metalwork for private homes, businesses, housing associations and public institutions in Billund Municipality and the surrounding area.'
                : 'Din lokale VVS-installatør siden 1963. VVS-, blik- og smedearbejde for private, erhverv, boligforeninger og offentlige institutioner i Billund Kommune og omegn.'}
            </p>
            <div className="space-y-1 text-xs text-slate-400 pt-1">
              <p>Montanavej 1B</p>
              <p>7190 Billund</p>
              <p>CVR 29407320</p>
            </div>
            <div className="space-y-1 text-xs pt-1">
              <p>
                <a href="tel:+4575331276" className="text-slate-200 hover:text-white transition-colors">
                  75 33 12 76
                </a>
              </p>
              <p>
                <a href="mailto:info@billundvvs.dk" className="text-slate-200 hover:text-white transition-colors">
                  info@billundvvs.dk
                </a>
              </p>
            </div>
          </div>

          {/* Col 2: Services / Ydelser */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-white mb-4">
              {isEn ? 'SERVICES' : 'YDELSER'}
            </h3>
            <ul className="space-y-2.5 text-xs">
              <li>
                <Link to={isEn ? '/en/plumbing' : '/vvs'} className="hover:text-white transition-colors">
                  {isEn ? 'Plumber in Billund' : 'VVS-installatør'}
                </Link>
              </li>
              <li>
                <Link to={isEn ? '/en/plumbing/bathrooms' : '/vvs/badevaerelser'} className="hover:text-white transition-colors">
                  {isEn ? 'Bathrooms' : 'Badeværelser'}
                </Link>
              </li>
              <li>
                <Link to={isEn ? '/en/heating/heat-pumps' : '/varme/varmepumper'} className="hover:text-white transition-colors">
                  {isEn ? 'Heat pumps' : 'Varmepumper'}
                </Link>
              </li>
              <li>
                <Link to={isEn ? '/en/heating/district-heating' : '/varme/fjernvarme'} className="hover:text-white transition-colors">
                  {isEn ? 'District heating' : 'Fjernvarme'}
                </Link>
              </li>
              <li>
                <Link to={isEn ? '/en/sheet-metal' : '/blikkenslager'} className="hover:text-white transition-colors">
                  {isEn ? 'Sheet metal' : 'Blikkenslager'}
                </Link>
              </li>
              <li>
                <Link to={isEn ? '/en/new-construction' : '/nybyggeri'} className="hover:text-white transition-colors">
                  {isEn ? 'New construction' : 'Nybyggeri'}
                </Link>
              </li>
              <li>
                <Link to={isEn ? '/en/service-maintenance' : '/service-og-vedligehold'} className="hover:text-white transition-colors">
                  {isEn ? 'Service & maintenance' : 'Service & vedligehold'}
                </Link>
              </li>
              <li>
                <Link to={isEn ? '/en/plumbing/emergency' : '/vvs/akut-vvs'} className="hover:text-white transition-colors">
                  {isEn ? 'Emergency plumbing' : 'Akut VVS'}
                </Link>
              </li>
            </ul>
          </div>

          {/* Col 3: Areas / Områder */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-white mb-4">
              {isEn ? 'AREAS' : 'OMRÅDER'}
            </h3>
            <ul className="space-y-2.5 text-xs">
              <li>
                <Link to={isEn ? '/en/plumber-billund' : '/vvs-billund'} className="hover:text-white transition-colors">
                  {isEn ? 'Plumber Billund' : 'VVS Billund'}
                </Link>
              </li>
              <li>
                <Link to={isEn ? '/en/plumber-grindsted' : '/vvs-grindsted'} className="hover:text-white transition-colors">
                  {isEn ? 'Plumber Grindsted' : 'VVS Grindsted'}
                </Link>
              </li>
              <li>
                <Link to={isEn ? '/en/plumber-give' : '/vvs-give'} className="hover:text-white transition-colors">
                  {isEn ? 'Plumber Give' : 'VVS Give'}
                </Link>
              </li>
              <li>
                <Link to={isEn ? '/en/plumber-vejle' : '/vvs-vejle'} className="hover:text-white transition-colors">
                  {isEn ? 'Plumber Vejle' : 'VVS Vejle'}
                </Link>
              </li>
              <li>
                <Link to={isEn ? '/en/plumber-soender-omme' : '/vvs-soender-omme'} className="hover:text-white transition-colors">
                  {isEn ? 'Plumber Sdr. Omme & Vorbasse' : 'VVS Sdr. Omme & Vorbasse'}
                </Link>
              </li>
            </ul>
          </div>

          {/* Col 4: Company / Firma */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-white mb-4">
              {isEn ? 'COMPANY' : 'FIRMA'}
            </h3>
            <ul className="space-y-2.5 text-xs">
              <li>
                <Link to={isEn ? '/en/about' : '/om-os'} className="hover:text-white transition-colors">
                  {isEn ? 'About us' : 'Om os'}
                </Link>
              </li>
              <li>
                <Link to={isEn ? '/en/references' : '/referencer'} className="hover:text-white transition-colors">
                  {isEn ? 'References' : 'Referencer'}
                </Link>
              </li>
              <li>
                <Link to={isEn ? '/en/careers' : '/karriere'} className="hover:text-white transition-colors">
                  {isEn ? 'Jobs & apprenticeships' : 'Job & lærlingepladser'}
                </Link>
              </li>
              <li>
                <Link to={isEn ? '/en/faq' : '/faq'} className="hover:text-white transition-colors">
                  {isEn ? 'FAQ' : 'FAQ'}
                </Link>
              </li>
              <li>
                <Link to={isEn ? '/en/guides' : '/guides'} className="hover:text-white transition-colors">
                  {isEn ? 'Guides & advice' : 'Guides & gode råd'}
                </Link>
              </li>
              <li>
                <Link to={isEn ? '/en/contact' : '/kontakt'} className="hover:text-white transition-colors">
                  {isEn ? 'Contact' : 'Kontakt'}
                </Link>
              </li>
              <li>
                <Link to={isEn ? '/en/privacy-policy' : '/privatlivspolitik'} className="hover:text-white transition-colors">
                  {isEn ? 'Privacy policy' : 'Privatlivspolitik'}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Garanti, Autorisation & Forsikring (Horisontal række) */}
        <div className="max-w-7xl mx-auto mt-12 pt-8 border-t border-slate-900/80">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-300 flex items-center gap-1.5 mb-4">
            <i className="fa-solid fa-shield-halved text-cyan-400" aria-hidden="true" />
            <span>{isEn ? 'Guarantee, Authorization & Insurance' : 'Garanti, Autorisation & Sikkerhed'}</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 items-stretch">
            {/* 1. TEKNIQ Approval Badge */}
            <a
              href="https://assets.auxodev.dk/assets/7f5366e1-5aaf-452f-9c72-7b11437bc331/aut-inst-2026-2028.jpg"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col justify-between group bg-white/5 hover:bg-white/10 p-2.5 rounded-lg border border-white/15 transition-all text-left"
              title="Autoriseret Installatørvirksomhed Godkendt 2026-2028 – TEKNIQ Kvalitet"
            >
              <div className="flex items-center justify-center bg-black/20 rounded p-1.5 mb-2">
                <img
                  src="https://assets.auxodev.dk/assets/7f5366e1-5aaf-452f-9c72-7b11437bc331/aut-inst-2026-2028.jpg"
                  alt="Autoriseret Installatørvirksomhed Godkendt 2026-2028 – TEKNIQ Kvalitet"
                  className="h-24 md:h-28 max-h-32 w-auto object-contain rounded"
                />
              </div>
              <div className="flex items-center justify-between text-[11px] text-slate-300 font-medium pt-1 border-t border-white/10">
                <span className="flex items-center gap-1.5">
                  <span className="size-1.5 rounded-full bg-emerald-400 inline-block animate-pulse"></span>
                  {isEn ? 'Authorised 2026–2028' : 'Autoriseret 2026–2028'}
                </span>
                <i className="fa-solid fa-arrow-up-right-from-square text-[9px] text-red-400 group-hover:translate-x-0.5 transition-transform" aria-hidden="true"></i>
              </div>
            </a>

            {/* 2. TEKNIQ Garantiordning Graphic Badge */}
            <a
              href="https://www.tekniq.dk/bliv-medlem/bliv-medlem-af-tekniq/medlemsfordele/garanti/garantiordning/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col justify-between group bg-white/5 hover:bg-white/10 p-2.5 rounded-lg border border-white/15 transition-all text-left"
              title={isEn ? 'TEKNIQ Guarantee Scheme – Covers up to DKK 150,000' : 'TEKNIQ Garantiordning – Dækker op til 150.000 kr.'}
            >
              <div className="flex-1 w-full flex items-center justify-center bg-white rounded p-3 mb-2 min-h-24 md:min-h-28">
                <img
                  src="https://assets.auxodev.dk/assets/7f5366e1-5aaf-452f-9c72-7b11437bc331/tekniq-sublogo-garanti-rgb-turkis.jpg"
                  alt={isEn ? 'TEKNIQ Guarantee Scheme' : 'TEKNIQ Garantiordning'}
                  className="max-h-16 md:max-h-20 w-auto object-contain"
                />
              </div>
              <div className="flex items-center justify-between text-[11px] text-slate-300 font-medium pt-1 border-t border-white/10">
                <span className="flex items-center gap-1.5">
                  <span className="size-1.5 rounded-full bg-cyan-400 inline-block"></span>
                  {isEn ? 'TEKNIQ Guarantee' : 'TEKNIQ Garantiordning'}
                </span>
                <i className="fa-solid fa-arrow-up-right-from-square text-[9px] text-cyan-400 group-hover:translate-x-0.5 transition-transform" aria-hidden="true"></i>
              </div>
            </a>

            {/* 3. Forsikret Service / Erhvervsansvarsforsikring */}
            <div className="flex flex-col justify-between bg-white/5 p-2.5 rounded-lg border border-white/15 text-left">
              <div>
                <div className="flex items-center gap-2.5 mb-1.5">
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#c5ecf8] shadow-sm">
                    <svg
                      className="size-4 text-[#081a2e]"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
                      <path d="m9 12 2 2 4-4" />
                    </svg>
                  </div>

                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <h4 className="text-[11px] font-bold text-white leading-tight">
                        {isEn ? 'Insured Service' : 'Forsikret Service'}
                      </h4>
                      <span className="text-[8px] font-semibold uppercase px-1 py-0.2 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                        {isEn ? 'Covered' : 'Dækket'}
                      </span>
                    </div>
                    <p className="text-[9.5px] text-cyan-300 leading-tight">
                      {isEn ? 'Public Liability Insurance' : 'Erhvervsansvarsforsikring'}
                    </p>
                  </div>
                </div>

                <p className="text-[10px] leading-snug text-slate-300 mt-1 line-clamp-2">
                  {isEn
                    ? 'Fully insured with public liability coverage – our customers are always completely covered.'
                    : 'Vi er selvfølgelig forsikret, hvis noget mod forventning skulle gå galt – kunder er altid dækket.'}
                </p>
              </div>

              <div className="flex items-center justify-between text-[11px] text-slate-300 font-medium pt-1 mt-1.5 border-t border-white/10">
                <span className="flex items-center gap-1.5 text-emerald-400">
                  <i className="fa-solid fa-circle-check text-[9px]" aria-hidden="true" />
                  <span>{isEn ? 'Customer safety guarantee' : 'Fuld tryghed for kunden'}</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Sub-Footer */}
        <div className="max-w-7xl mx-auto mt-12 pt-8 border-t border-slate-900 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-slate-500">
          <div>
            {isEn
              ? '© 2026 Billund Varme & Sanitet A/S - Authorised plumbing contractor'
              : '© 2026 Billund Varme & Sanitet A/S - Autoriseret VVS-installatør'}
          </div>
          <div>
            {isEn
              ? 'Opening hours: Mon–Thu 7:00–16:00 · Fri 7:00–13:00 · Emergency service every day'
              : 'Åbningstider: Man–tors 7.00–16.00 · Fre 7.00–13.00 · Døgnvagt alle dage'}
          </div>
        </div>
      </footer>
    </div>
  );
}
